/* ==========================================================================
   ECOFUEGO — PDP tile gallery zoom  ·  ecofuego-pdp-gallery.js
   --------------------------------------------------------------------------
   Loaded once per page from sections/main-product.liquid, on `pdp-` templates
   only. Pairs with snippets/ef-pdp-media-grid.liquid.

   One job: clicking a square tile opens that photograph full size in a
   <dialog>, with arrow keys to move between them.

   WHY NOT DAWN'S product-modal
   ----------------------------
   Dawn's lightbox is wired to the gallery markup that the hero-video format
   deliberately stops rendering. Calling into it from the tile grid would
   couple this format to a component it has replaced — and the first time
   Dawn's modal changed, a page with no gallery would break in a way nobody
   would think to look for.

   WHY EVENT DELEGATION
   --------------------
   One listener on the grid rather than one per tile. Variant Swatch King and
   LimeSpot both re-render pieces of this page after load; a listener bound to
   each button at DOMContentLoaded would be lost on any tile that got
   replaced, and the failure is silent — the tile simply stops opening.

   FAILURE MODE
   ------------
   If this file never loads, the tiles are inert buttons and the grid is still
   a readable set of photographs. Nothing is hidden behind the interaction, so
   the floor is "you cannot zoom", not "you cannot see".
   ========================================================================== */

(function () {
  'use strict';

  var grid = document.querySelector('[data-ef-gallery]');
  var dialog = document.querySelector('[data-ef-gallery-dialog]');
  if (!grid || !dialog) return;

  var img = dialog.querySelector('[data-ef-gallery-img]');
  var closeBtn = dialog.querySelector('[data-ef-gallery-close]');
  var prevBtn = dialog.querySelector('[data-ef-gallery-prev]');
  var nextBtn = dialog.querySelector('[data-ef-gallery-next]');
  var count = dialog.querySelector('[data-ef-gallery-count]');
  if (!img) return;

  // Live-queried rather than cached: see the delegation note above — the tile
  // list can change after load.
  function tiles() {
    return Array.prototype.slice.call(grid.querySelectorAll('[data-ef-gallery-open]'));
  }

  var current = -1;
  // The tile that opened the dialog, so focus can go back where it came from.
  // Losing focus to the top of the document is the single most common
  // accessibility defect in a hand-rolled lightbox.
  var opener = null;

  function show(index) {
    var all = tiles();
    if (!all.length) return;
    // Wrap at both ends. A gallery is a ring, not a line.
    if (index < 0) index = all.length - 1;
    if (index >= all.length) index = 0;
    current = index;

    var tile = all[index];
    img.src = tile.getAttribute('data-ef-full') || '';
    img.alt = tile.getAttribute('data-ef-alt') || '';
    if (count) count.textContent = index + 1 + ' / ' + all.length;
  }

  function open(index, fromEl) {
    opener = fromEl || null;
    show(index);
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      // No <dialog> support: the href-less fallback is to do nothing rather
      // than fake a modal badly. Extremely rare, and the tile grid is intact.
      dialog.setAttribute('open', '');
    }
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
    // Release the image so a large file is not held in memory behind a closed
    // dialog — these are requested at 1800px.
    img.src = '';
    if (opener && typeof opener.focus === 'function') opener.focus();
    opener = null;
  }

  grid.addEventListener('click', function (e) {
    var tile = e.target.closest ? e.target.closest('[data-ef-gallery-open]') : null;
    if (!tile || !grid.contains(tile)) return;
    e.preventDefault();
    open(tiles().indexOf(tile), tile);
  });

  /* Tapping the hero film opens the gallery at the first photograph
     (Gabe, 2026-09-12).

     The film is ambient — muted, looping, no information in the audio — so a
     tap on it is not "play/pause", it is "show me this thing properly". The
     video element has `pointer-events: none` in CSS so the tap reaches this
     handler instead of the native control surface, and the hero drops
     `data-ef-video-controls` for the same reason: two different things
     claiming the same tap is how you get a control bar instead of a gallery.

     Phone only in practice, because the film is hidden above 750px. The guard
     asks whether the TILE GRID is visible, not whether the dialog is — a
     closed <dialog> has `display: none` from the UA stylesheet at all times,
     so guarding on the dialog meant the condition was always true and the
     gallery never opened. It failed silently and looked like a dead tap.
     Caught 2026-09-12 on the first tap of the first build.

     The grid is the right proxy anyway: it is the thing the media query
     actually toggles, and "are the tiles in play on this breakpoint" is
     precisely the question being asked. */
  var hero = document.querySelector('.ef-pdp-hero');
  if (hero) {
    hero.addEventListener('click', function () {
      if (getComputedStyle(grid).display === 'none') return;
      open(0, hero);
    });
  }

  if (closeBtn) closeBtn.addEventListener('click', close);
  if (prevBtn) prevBtn.addEventListener('click', function () { show(current - 1); });
  if (nextBtn) nextBtn.addEventListener('click', function () { show(current + 1); });

  /* ── Swipe ────────────────────────────────────────────────────────────────
     Horizontal drag moves through the photographs (Gabe, 2026-09-12). Three
     decisions worth keeping:

     1. The gesture is measured, not assumed. A swipe only counts when it
        travels 40px+ horizontally AND further horizontally than vertically.
        Without the second test every attempt to scroll the page inside the
        dialog jumps the image instead, which feels broken rather than
        sensitive.

     2. `touchmove` is NOT preventDefault-ed. Doing so would kill pinch-zoom
        on the photograph, and pinching a product photo is the whole reason
        someone opened it.

     3. Listeners are passive. A non-passive touch listener on a scrollable
        surface makes the browser wait for JS before it can scroll, which is
        the classic source of sticky-feeling touch.
     -------------------------------------------------------------------------- */
  var touchX = null;
  var touchY = null;

  dialog.addEventListener(
    'touchstart',
    function (e) {
      if (e.touches.length !== 1) return; // a pinch is not a swipe
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
    },
    { passive: true }
  );

  dialog.addEventListener(
    'touchend',
    function (e) {
      if (touchX === null || !e.changedTouches.length) return;
      var dx = e.changedTouches[0].clientX - touchX;
      var dy = e.changedTouches[0].clientY - touchY;
      touchX = null;
      touchY = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
      show(dx < 0 ? current + 1 : current - 1);
    },
    { passive: true }
  );

  // Backdrop click. The dialog element fills the viewport, so a click landing
  // on the dialog itself (rather than on the image or the close button) is a
  // click on the backdrop.
  dialog.addEventListener('click', function (e) {
    if (e.target === dialog) close();
  });

  dialog.addEventListener('cancel', function () {
    // Native Escape. Let it close, but run our own teardown.
    img.src = '';
    if (opener && typeof opener.focus === 'function') opener.focus();
    opener = null;
  });

  dialog.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      show(current + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      show(current - 1);
    }
  });
})();
